import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ERRORS } from '@/constants';
import { notificationsService } from './service';
import type { NotificationTab } from './types';

/**
 * Server state for notifications.
 *
 * One root key for the list and the badge, so marking something read
 * refreshes both - the badge cannot go on claiming an unread item the list
 * already shows as read.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (tab: NotificationTab) => [...notificationKeys.all, 'list', tab] as const,
  pending: () => [...notificationKeys.all, 'pending'] as const,
};

const PAGE_SIZE = 20;

export function useNotifications(tab: NotificationTab) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(tab),
    queryFn: ({ pageParam, signal }) =>
      notificationsService.list(
        { unreadOnly: tab === 'unread', limit: PAGE_SIZE, offset: pageParam },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) =>
      last.meta.hasMore ? last.meta.offset + last.data.length : undefined,
    staleTime: 30_000,
  });
}

/**
 * The unread count behind the sidebar badge.
 *
 * Polled, because nothing pushes to the browser: a dentist's decision arrives
 * whenever they make it. A minute is often enough to notice and rare enough
 * that a tab left open all day costs nothing.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.pending(),
    queryFn: ({ signal }) => notificationsService.pending(signal),
    select: (pending) => pending.unreadTotal,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** Marks the given notifications read - or every unread one, with no ids. */
export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids?: string[]) => notificationsService.markRead(ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : ERRORS.generic);
    },
  });
}
